/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
/**
 * UnifiedCourseCard
 *
 * @file UnifiedCourseCard.jsx
 * @author Anuja Gaikwad
 * @since 2.0.0
 */

import React, { Fragment, useId } from 'react';
import PropTypes from 'prop-types';
import { Menu, Typography } from '@mui/material';
import { BUTTON_TYPES, UNIFIED_COURSE_CARD_CLASSES } from '../../common/constants';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import Download from '../download/Download';
import Icon from '../icons/Icon';
import {
  CourseHeaderHotspot,
  CourseHeaderIcon,
  CourseHeaderMenuItem,
  CourseHeaderMenuList,
  CourseHeaderMenuPaper,
  CourseHeaderUnifiedActions,
  CourseHeaderUnifiedBadge,
  CourseHeaderUnifiedBookActionButton,
  CourseHeaderUnifiedBookTitle,
  CourseHeaderUnifiedChip,
  CourseHeaderUnifiedContent,
  CourseHeaderUnifiedCourseTitle,
  CourseHeaderUnifiedCoverContainer,
  CourseHeaderUnifiedCoverImage,
  CourseHeaderUnifiedCoverInfoButton,
  CourseHeaderUnifiedDetails,
  CourseHeaderUnifiedDownloadRoot,
  CourseHeaderUnifiedHeading,
  CourseHeaderUnifiedIconButton,
  CourseHeaderUnifiedInviteStudentsButton,
  CourseHeaderUnifiedLmsStatus,
  CourseHeaderUnifiedMetaRow,
  CourseHeaderUnifiedRoot,
  CourseHeaderUnifiedSecondaryButton,
  CourseHeaderUnifiedSettingsGroup,
  CourseHeaderUnifiedTitleButton
} from './styles/CourseHeaderStyles';

const joinText = (...parts) => parts
  .map((part) => (typeof part === 'string' ? part.trim() : part))
  .filter(Boolean)
  .join(' ');

const UnifiedCourseCard = (props) => {
  const {
    course = {},
    book = {},
    config = {},
    callback = {},
    className,
    classes: classesProp = {}
  } = props;

  const {
    showCourseTitle = true,
    showBookTitle = true,
    showBookCover = true,
    showTitleInfo = true,
    showDownload = false,
    showMetaData = true,
    showBookActions = true,
    showCourseMenu = true,
    showInviteStudents = true
  } = config;

  const { handleMenuOpenChange, handleCardClick, handleSettingsClick } = callback;
  const menuId = useId();
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const hasCover = Boolean(showBookCover && book.coverImage);
  const isClickable = typeof handleCardClick === 'function' && Boolean(showCourseTitle && course.title);

  const slotClassName = (slot, ...extra) => [
    UNIFIED_COURSE_CARD_CLASSES[slot],
    classesProp[slot],
    ...extra
  ].filter(Boolean).join(' ');

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
    handleMenuOpenChange?.();
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const renderTitleInfo = () => {
    if (!showTitleInfo || !book.titleInfo) {
      return null;
    }

    const { tooltip, callback: bookTitleInfoCallback, showHotspot = false } = book.titleInfo;

    return (
      <TooltipWrapper tooltip={tooltip || ''} placement="top">
        <CourseHeaderUnifiedCoverInfoButton
          className={slotClassName('infoButton')}
          onClick={() => {
            bookTitleInfoCallback?.();
          }}
          aria-label={tooltip || undefined}
          aria-haspopup="dialog"
        >
          {showHotspot && <CourseHeaderHotspot />}
          <CourseHeaderIcon name="InfoIcon" />
        </CourseHeaderUnifiedCoverInfoButton>
      </TooltipWrapper>
    );
  };

  const renderBookCover = () => {
    if (!hasCover) {
      return null;
    }

    const { progress, status, onCancelClick, onBookClick } = book?.titleInfo || {};

    return (
      <CourseHeaderUnifiedCoverContainer className={slotClassName('cover')}>
        {showDownload ? (
          <CourseHeaderUnifiedDownloadRoot>
            <Download
              backgroundImage={book?.coverImage}
              progress={progress}
              downloadStatus={status}
              onCancelClick={onCancelClick}
              onClick={onBookClick}
            />
          </CourseHeaderUnifiedDownloadRoot>
        ) : (
          <CourseHeaderUnifiedCoverImage
            className={slotClassName('coverImage')}
            src={book.coverImage}
            alt={book.title ? `Book cover for ${book.title}` : 'Book cover'}
          />
        )}
        {renderTitleInfo()}
      </CourseHeaderUnifiedCoverContainer>
    );
  };

  const renderCourseTitle = () => {
    if (!showCourseTitle || !course.title) {
      return null;
    }

    return (
      <CourseHeaderUnifiedCourseTitle className={slotClassName('title')}>
        {isClickable ? (
          <CourseHeaderUnifiedTitleButton
            type="button"
            className={slotClassName('titleButton')}
            onClick={handleCardClick}
          >
            {course.title}
          </CourseHeaderUnifiedTitleButton>
        ) : course.title}
      </CourseHeaderUnifiedCourseTitle>
    );
  };

  const renderMetaData = () => {
    if (!showMetaData || !course.metaData?.length) {
      return null;
    }

    const badgeItems = course.metaData.filter((meta) => meta.badge && meta.info);
    const dateItems = course.metaData.filter((meta) => !meta.badge && meta.info);

    if (!badgeItems.length && !dateItems.length) {
      return null;
    }

    return (
      <CourseHeaderUnifiedMetaRow className={slotClassName('metaRow')}>
        {badgeItems.map((meta) => (
          <CourseHeaderUnifiedBadge key={meta.label || meta.info} className={slotClassName('badge')}>
            {joinText(meta.info)}
          </CourseHeaderUnifiedBadge>
        ))}
        {dateItems.map((meta) => (
          <CourseHeaderUnifiedChip key={meta.label || meta.info} className={slotClassName('chip')}>
            {joinText(meta.label, meta.info)}
          </CourseHeaderUnifiedChip>
        ))}
      </CourseHeaderUnifiedMetaRow>
    );
  };

  const renderBookAction = (action) => {
    if (!action.callback || !action.text) {
      return null;
    }

    const actionClassName = slotClassName('bookAction', action.className);

    if (action.type === BUTTON_TYPES.BUTTON) {
      return (
        <CourseHeaderUnifiedSecondaryButton
          key={action.text}
          className={actionClassName}
          onClick={action.callback}
        >
          {action.text}
        </CourseHeaderUnifiedSecondaryButton>
      );
    }

    if (!action.type) {
      return (
        <CourseHeaderUnifiedBookActionButton
          key={action.text}
          className={actionClassName}
          onClick={() => action.callback(action)}
          startIcon={action.iconName ? <CourseHeaderIcon name={action.iconName} /> : null}
        >
          {action.text}
        </CourseHeaderUnifiedBookActionButton>
      );
    }

    return null;
  };

  const renderCourseMenu = () => (
    <Menu
      id={menuId}
      className={slotClassName('menu')}
      anchorEl={menuAnchorEl}
      keepMounted
      open={isMenuOpen}
      onClose={handleMenuClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      slots={{
        paper: CourseHeaderMenuPaper,
        list: CourseHeaderMenuList
      }}
    >
      {course.courseMenu.map((item) => (
        <CourseHeaderMenuItem
          key={item.label}
          className={slotClassName('menuItem', item.className)}
          disableRipple
          disableGutters
          hasDivider={item.divider}
          onClick={() => {
            handleMenuClose();
            item.callback(item);
          }}
        >
          <Typography>{item.label}</Typography>
        </CourseHeaderMenuItem>
      ))}
    </Menu>
  );

  const renderSettingsButton = () => {
    if (typeof handleSettingsClick !== 'function') {
      return null;
    }

    return (
      <CourseHeaderUnifiedIconButton
        className={slotClassName('settingsButton')}
        onClick={handleSettingsClick}
        aria-label={course.textInfo?.settingsLabel}
      >
        <Icon name="SettingsIcon" />
      </CourseHeaderUnifiedIconButton>
    );
  };

  const renderMenuButton = () => {
    if (!showCourseMenu || !course.courseMenu?.length) {
      return null;
    }

    return (
      <Fragment>
        <CourseHeaderUnifiedIconButton
          className={slotClassName('menuButton')}
          onClick={handleMenuOpen}
          aria-label={course.textInfo?.menuLabel}
          aria-haspopup="menu"
          aria-controls={isMenuOpen ? menuId : undefined}
          aria-expanded={isMenuOpen ? 'true' : 'false'}
        >
          <Icon name="CaretDownFilledIcon" />
        </CourseHeaderUnifiedIconButton>
        {renderCourseMenu()}
      </Fragment>
    );
  };

  const renderSettingsControls = () => {
    const settingsButton = renderSettingsButton();
    const menuButton = renderMenuButton();

    if (!settingsButton && !menuButton) {
      return null;
    }

    return (
      <CourseHeaderUnifiedSettingsGroup>
        {settingsButton}
        {menuButton}
      </CourseHeaderUnifiedSettingsGroup>
    );
  };

  const renderLmsStatusOrInvite = () => {
    if (course.lmsLink?.isLinked) {
      return (
        <CourseHeaderUnifiedLmsStatus className={slotClassName('lmsStatus')}>
          <Icon name="LinkIcon" />
          {course.lmsLink.text}
        </CourseHeaderUnifiedLmsStatus>
      );
    }

    if (!showInviteStudents || !course.inviteStudents) {
      return null;
    }

    return (
      <CourseHeaderUnifiedInviteStudentsButton
        className={slotClassName('inviteStudents')}
        onClick={course.inviteStudents.callback}
      >
        {course.inviteStudents.text}
      </CourseHeaderUnifiedInviteStudentsButton>
    );
  };

  const renderActions = () => {
    const bookActions = (showBookActions ? book?.actions || [] : [])
      .map((action) => renderBookAction(action))
      .filter(Boolean);
    const lmsStatusOrInvite = renderLmsStatusOrInvite();
    const settingsControls = renderSettingsControls();

    if (!bookActions.length && !lmsStatusOrInvite && !settingsControls) {
      return null;
    }

    return (
      <CourseHeaderUnifiedActions className={slotClassName('actions')}>
        {bookActions}
        {lmsStatusOrInvite}
        {settingsControls}
      </CourseHeaderUnifiedActions>
    );
  };

  return (
    <CourseHeaderUnifiedRoot
      className={slotClassName('root', isClickable && slotClassName('clickable'), className)}
      hasCover={hasCover}
      isClickable={isClickable}
    >
      {renderBookCover()}
      <CourseHeaderUnifiedContent className={slotClassName('content')}>
        <CourseHeaderUnifiedHeading className={slotClassName('heading')}>
          {renderCourseTitle()}
          {renderMetaData()}
        </CourseHeaderUnifiedHeading>
        <CourseHeaderUnifiedDetails className={slotClassName('details')}>
          {showBookTitle && book.title && (
            <CourseHeaderUnifiedBookTitle className={slotClassName('bookTitle')}>
              {book.title}
            </CourseHeaderUnifiedBookTitle>
          )}
          {renderActions()}
        </CourseHeaderUnifiedDetails>
      </CourseHeaderUnifiedContent>
    </CourseHeaderUnifiedRoot>
  );
};

UnifiedCourseCard.propTypes = {
  course: PropTypes.shape({
    title: PropTypes.string,
    metaData: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      info: PropTypes.string,
      badge: PropTypes.bool
    })),
    courseMenu: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      callback: PropTypes.func,
      divider: PropTypes.bool,
      className: PropTypes.string
    })),
    inviteStudents: PropTypes.shape({
      text: PropTypes.string,
      callback: PropTypes.func
    }),
    lmsLink: PropTypes.shape({
      isLinked: PropTypes.bool,
      text: PropTypes.string
    }),
    textInfo: PropTypes.shape({
      settingsLabel: PropTypes.string,
      menuLabel: PropTypes.string
    })
  }),
  book: PropTypes.object,
  config: PropTypes.object,
  callback: PropTypes.shape({
    handleMenuOpenChange: PropTypes.func,
    handleCardClick: PropTypes.func,
    handleSettingsClick: PropTypes.func
  }),
  className: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string)
};

UnifiedCourseCard.defaultProps = {
  course: {},
  book: {},
  config: {},
  callback: {},
  className: undefined,
  classes: {}
};

export default UnifiedCourseCard;
